import { observer } from "mobx-react-lite";
import { useStore } from "../../store";
import { Choice } from "../../types/game";
import { useABButtons } from "../../hooks/useABButtons";
import { TextButton } from "../../components/TextButton/TextButton";
import { Header } from "../../components/Header/Header";
import { SizeCard } from "../../components/SizeCard/SizeCard";
import Card from "../../components/Card/Card";
import InactivityManager from "../../components/InactivityManager/InactivityManager";
import { useTranslation } from "react-i18next";
import { ViewShell } from "../../components/Layout/ViewShell";

export const ChoiceView = observer(() => {
  const { mainStore } = useStore();
  const currentNode = mainStore.getCurrentNode();
  const { promptState, resetInactivity } = useABButtons();
  const { t } = useTranslation();

  function onButtonAClick() {
    mainStore.makeChoice("a");
    resetInactivity();
  }

  function onButtonBClick() {
    mainStore.makeChoice("b");
    resetInactivity();
  }

  const node = currentNode;
  if (!node) {
    return null;
  }

  const { id, optionA, optionB } = node as Choice;

  const isSizeChoice = optionA.cardHeader && optionB.cardHeader;

  const stepCount = mainStore.getCurrentStepCount;
  let legend = "";

  const isValidStepCount = (steps: number) => {
    return [1, 2, 3].includes(steps);
  };

  if (isValidStepCount(stepCount)) {
    legend = t("step", { stepCount: stepCount.toString() });
  } else {
    legend = t("lastDetail");
  }

  const renderSizeChoice = () => (
    <>
      <div className="choice-view__option choice-view__option--size">
        <SizeCard size="small" onClick={onButtonAClick} />
        <div className="choice-view__cta">
          <TextButton
            label="A"
            id="a-button"
            text={(currentNode as Choice).optionA.text}
            onClick={onButtonAClick}
            isChoiceView={true}
          />
        </div>
      </div>

      <div className="choice-view__option choice-view__option--size">
        <SizeCard size="large" onClick={onButtonBClick} />
        <div className="choice-view__cta">
          <TextButton
            label="B"
            id="b-button"
            text={(currentNode as Choice).optionB.text}
            onClick={onButtonBClick}
            isChoiceView={true}
          />
        </div>
      </div>
    </>
  );

  const renderStandardChoice = () => (
    <>
      <div className="choice-view__option">
        <Card
          title=""
          description=""
          imageUrl={(currentNode as Choice).optionA.image || ""}
          onClick={onButtonAClick}
          size="lg"
        />
        <div className="choice-view__cta">
          <TextButton
            label="A"
            id="a-button"
            text={isValidStepCount(stepCount) ? (currentNode as Choice).optionA.text : ""}
            onClick={onButtonAClick}
            isChoiceView={true}
          />
        </div>
      </div>
      {(currentNode as Choice).optionB.text && (currentNode as Choice).optionB.image ? (
        <div className="choice-view__option">
          <Card
            title=""
            description=""
            imageUrl={(currentNode as Choice).optionB.image || ""}
            onClick={onButtonBClick}
            size="lg"
          />
          <div className="choice-view__cta">
            <TextButton
              label="B"
              id="b-button"
              text={isValidStepCount(stepCount) ? (currentNode as Choice).optionB.text : ""}
              onClick={onButtonBClick}
              isChoiceView={true}
            />
          </div>
        </div>
      ) : null}
    </>
  );

  return (
    <ViewShell
      variant="choice"
      id={id}
      aria-labelledby={`${id}-title`}
      data-variant={isSizeChoice ? "size" : "standard"}
    >
      <div className="view-shell__content choice-view__content">
        <div className="view-shell__title" id={`${id}-title`}>
          <Header legend={legend} title={currentNode.title} />
        </div>

        <div className="choice-view__options">
          {isSizeChoice ? renderSizeChoice() : renderStandardChoice()}
        </div>
      </div>

      <InactivityManager promptState={promptState} resetInactivity={resetInactivity} />
    </ViewShell>
  );
});
